import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { REQUEST_STATUS } from "../constants/status";

import type {
  UpdateVacationRequestResultT,
  UpdateVacationRequestsDataT,
} from "../domain/types/updateVacationRequests.types";
import type { VacationRequestEntityT } from "../domain/entities/vacationRequest.entity";
import type { VacationRequestRowUiT } from "../domain/ui/vacationRequestRow.model";
import type { VacationRequestsRepositoryT } from "../domain/ports/vacationRequests.repository";

import { applyVacationRequests } from "../application/useCases/applyVacationRequests";
import { getVacationRequests } from "../application/useCases/getVacationRequests";
import { updateVacationRequest } from "../application/useCases/updateVacationRequest";

import {
  getProcessedBy,
  mapOutUpdatedRequests,
  sortAuditHistoryAsc,
} from "./helpers";

const toUiModel = (
  requests: VacationRequestEntityT[],
): VacationRequestRowUiT[] =>
  requests.map((request) => ({
    id: request.id,
    code: request.collaborator?.code,
    fullName: request.collaborator?.fullName,
    requestType: request.type?.name,
    totalDays: request.totalDays,
    processedBy: getProcessedBy(request),
    startDate: request.startDate,
    endDate: request.endDate,
    status: REQUEST_STATUS[request.status],
    audit: request.audit
      ? { history: sortAuditHistoryAsc(request.audit?.history) }
      : null,
  }));

type CreateUseVacationRequestsParamsT = {
  vacationRequestsRepository: VacationRequestsRepositoryT;
};

type UseVacationRequestsParamsT = {
  colaboraInstanceId: string;
  enabled?: boolean;
  startDate?: string | null;
  endDate?: string | null;
  periodTypeId?: string | null;
};

export const createUseVacationRequests = ({
  vacationRequestsRepository,
}: CreateUseVacationRequestsParamsT) => {
  return function useVacationRequests({
    colaboraInstanceId,
    enabled = true,
    startDate = null,
    endDate = null,
    periodTypeId = null,
  }: UseVacationRequestsParamsT) {
    const queryClient = useQueryClient();

    const vacationRequestsQuery = useQuery({
      queryKey: [
        "vacations/requests",
        { colaboraInstanceId, startDate, endDate, periodTypeId },
      ],
      queryFn: () =>
        getVacationRequests({
          vacationRequestsRepository,
          colaboraInstanceId,
          startDate,
          endDate,
          periodTypeId,
        }),
      select: toUiModel,
      enabled: enabled && !!colaboraInstanceId,
    });

    const updateVacationRequestMutation = useMutation({
      mutationFn: ({
        requestsToUpdate,
      }: {
        requestsToUpdate: UpdateVacationRequestsDataT[];
      }) =>
        updateVacationRequest({
          vacationRequestsRepository,
          data: requestsToUpdate,
          colaboraInstanceId,
        }),
      onSuccess: ({ succeeded }: UpdateVacationRequestResultT) => {
        if (succeeded.length === 0) return;

        queryClient.setQueryData<VacationRequestEntityT[]>(
          [
            "vacations/requests",
            { colaboraInstanceId, startDate, endDate, periodTypeId },
          ],
          (oldData) => {
            if (!oldData) return oldData;
            return mapOutUpdatedRequests(oldData, succeeded);
          },
        );
      },
    });

    const applyRequestsMutation = useMutation({
      mutationFn: ({ requestIds }: { requestIds: string[] }) =>
        applyVacationRequests({
          vacationRequestsRepository,
          requestIds,
        }),
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [
            "vacations/requests",
            { colaboraInstanceId, startDate, endDate, periodTypeId },
          ],
        });
      },
    });

    return {
      ...vacationRequestsQuery,
      updateVacationRequest: updateVacationRequestMutation,
      applyRequests: applyRequestsMutation,
    };
  };
};
